import { useEffect, useRef, lazy, Suspense } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   HiddenMapPrewarmer — Renders CircuitMap offscreen to pre-warm map tiles.

   Google 3D Tiles and MapLibre tiles stream based on viewport. By mounting
   the map hidden at the circuit's final viewport (zoom 15, pitch 50°) for
   ~15 seconds, we force the browser (and Service Worker) to cache the tiles
   that the cinematic animation will need.

   The component unmounts after the timeout and calls onComplete.
   ═══════════════════════════════════════════════════════════════════════════ */

const CircuitMap = lazy(() => import('./CircuitMap'));

/** How long to let the map stream tiles before declaring pre-warm done (ms) */
const PREWARM_DURATION_MS = 15_000;

interface HiddenMapPrewarmerProps {
  trackPath: number[][];
  center: [number, number];
  mapStyleId: string;
  trackColor: string;
  onComplete: () => void;
}

export default function HiddenMapPrewarmer({
  trackPath,
  center,
  mapStyleId,
  trackColor,
  onComplete,
}: HiddenMapPrewarmerProps) {
  const completedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, PREWARM_DURATION_MS);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        opacity: 0,
        pointerEvents: 'none',
        zIndex: -1,
      }}
      aria-hidden="true"
    >
      <Suspense fallback={null}>
        <CircuitMap
          trackPath={trackPath}
          center={center}
          zoom={15}
          pitch={50}
          bearing={0}
          animate={false}
          trackColor={trackColor}
          mapStyleId={mapStyleId}
          style={{ width: '100%', height: '100%' }}
        />
      </Suspense>
    </div>
  );
}
